import React, {useContext, useEffect, useMemo} from 'react';
import { withRouter, useParams } from 'react-router-dom';
import {Formik} from "formik";
import {Button, Form} from "react-bootstrap";
import {ButtonLink, FormButtonGroup, FormikControl, FormRowGroup, Loading, PageHeader} from "./Components";
import TonersInfo from "./TonersInfo";
import type {PrinterEditable, PrinterWebInfo} from "../services/API";
import {RouteComponentProps} from "react-router-dom";
import API, {DataAPI, DbHandler, MutationAPI, useData, useMutator} from "../services/API";
import {SessionContext} from "./SessionContext";
import PrinterNotifications from "./PrinterNotifications";
import PrinterPanels from "./PrinterPanels";
import {PrinterStatusBadge} from "./PrinterStates";
import {formatDate, Validation} from "../commons/Utils";
import {Helmet} from "react-helmet";
import DeletePrinter from "./modals/DeletePrinter";
import {useQueryClient, UseMutationResult} from "react-query";
import Notifications from "./Notifications";
import {Copy} from "./Font";

const copyToClipboard = (value: string) => {
  if (!value) return Promise.resolve(false);

  if (window.navigator && window.navigator.clipboard && window.navigator.clipboard.writeText) {
    return window.navigator.clipboard.writeText(value).then(() => true);
  }

  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.value = value;
    input.setAttribute('readonly', 'readonly');
    input.style.position = 'absolute';
    input.style.left = '-9999px';
    document.body.appendChild(input);
    input.select();

    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (e) {
      copied = false;
    }

    document.body.removeChild(input);
    resolve(copied);
  });
}

const ManualCounterBadge = () => (
  <span className="manual-counter-badge" title="Ostatni zapis liczników został wprowadzony ręcznie.">
    Ręczny licznik
  </span>
)

const PrintersDetails = (props: RouteComponentProps) => {

  const ctx = useContext(SessionContext);

  const { id } = useParams();
  const { isLoading, error, data, isFetching, key, invalidate } = useData(id, 'printer', DataAPI.getPrinterDetails); //, { refetchInterval: 1000 }

  const updatePrinter: UseMutationResult<PrinterWebInfo> = useMutator(MutationAPI.updatePrinter, {
    customMsg: 'Zmiany zostały zapisane.', afterSuccess: invalidate
  });

  //jeśli zrobimy alias przy destrukturyzacji nie będzie można ustawić typu
  let printer: PrinterWebInfo = data;

  const queryClient = useQueryClient();

  useEffect(()=>{
    if (printer) {
      const handler: DbHandler = API.clientApi.registerHandler('printer', (change, id)=> {
        if (id === printer.id) queryClient.invalidateQueries(key).then(()=>{});
      });
      return ()=>{
        handler.unregister();
      }
    }
  }, [printer]);

  const submit = (pwi: PrinterWebInfo, {setSubmitting}) => {
    setSubmitting(true);

    let value: PrinterEditable = {};
    value.id = pwi.id;
    value.name = pwi.name;
    value.manualInactive = !!pwi.manualInactive;
    value.nis = pwi.nis;
//    value.clientId = pwi.clientId;
    if (!pwi.lwi || !pwi.lwi.id) value.lwi = null;
    else value.lwi = pwi.lwi;
    value.description = pwi.description;
    value.additionalData = pwi.additionalData;


    updatePrinter.mutate(value, { onSettled: ()=>setSubmitting(false)})
  }

  const validate = (values) => {
    let err = {};
    if (values.name && values.name.length > 64) err.name = 'Długość nazwy nie może przekraczać 64 znaków';
    Validation.add(err, 'nis', Validation.length(values.nis, 64));
//    Validation.add(err, 'clientId', Validation.length(values.clientId, 64));
    Validation.add(err, 'description', Validation.length(values.description, 254));
    return err;
  }

  const getLocations = (filter: string) => {
    return API.getLocations(filter).then(res=>res.data);
  }

  const initial = useMemo(() => {
    if (!printer) return null;
    return {
      id: printer.id,
      name: printer.name || '',
      manualInactive: !!printer.manualInactive,
      nis: printer.nis || '',
      model: printer.model || '',
//      clientId: printer.clientId || '',
      lwi: printer.lwi && printer.lwi.id ? printer.lwi : null,
      description: printer.description || '',
      additionalData: printer.additionalData ? printer.additionalData.map((entry) => ({...entry})) : [],
    };
  }, [printer]);
  const serviceCauses = printer && printer.serviceCauses ? printer.serviceCauses.filter(Boolean) : [];
  const jamLocations = printer && printer.jamLocations ? printer.jamLocations.filter(Boolean) : [];
  const offlineCauses = printer && printer.offlineCauses ? printer.offlineCauses.filter(Boolean) : [];
  const alertsList = printer && printer.alerts ? printer.alerts.filter((alert) => {
    if (!alert) return false;
    const trimmed = alert.trim();
    if (!trimmed) return false;
    if (trimmed.toLowerCase() === 'wymagany serwis' || trimmed.toLowerCase() === 'wezwij serwis') return false;
    return !serviceCauses.includes(trimmed);
  }) : [];
  const alertsFormatted = alertsList.join(', ');
  const serviceCausesFormatted = serviceCauses.join(', ');
  const hasTonerData = printer ? [printer.tonerBlack, printer.tonerCyan, printer.tonerMagenta, printer.tonerYellow]
    .some(value => value !== null && value !== undefined) : false;
  const handleCopySerialNumber = () => {
    copyToClipboard(printer.serialNumber).then((copied) => {
      Notifications.pushNew(copied ? 'Numer seryjny został skopiowany.' : 'Nie udało się skopiować numeru seryjnego.', copied ? 'success' : 'warning');
    });
  }

  const backTo = (props.location && props.location.state && props.location.state.listSearch) || '/printers';

  if (isLoading) return <Loading />;
  if (error) return <p>{error.message}</p>;

  return <>
      <Helmet>
        <title>{printer.name}</title>
      </Helmet>
      <PageHeader title={printer.name}>
        <PrinterStatusBadge states={printer.errorStates} serviceCauses={printer.serviceCauses} jamLocations={printer.jamLocations} offlineCauses={printer.offlineCauses}/>
        {printer.manualCounter ? <ManualCounterBadge /> : null}
        {isFetching ? <span className="page-header-fetching">aktualizowanie...</span> : null}
        <ButtonLink variant="link" to={backTo} className="action-icon-btn">
          <i className="fas fa-arrow-left"/> <span>Powrót</span>
        </ButtonLink>
      </PageHeader>
      <div className="printer-details-grid">
        <div className="settings-section">
          <h2>Szczegóły</h2>
          <Formik initialValues={initial} onSubmit={submit} validate={validate} enableReinitialize={true}>
            {({handleSubmit, isSubmitting, handleReset, isValid}) => (
                <Form noValidate onSubmit={handleSubmit} className="form printer-form">
                  <FormRowGroup controlId="nameId" label="Nazwa">
                    <FormikControl type="text" name="name" maxLength={64}/>
                  </FormRowGroup>
                  <FormRowGroup controlId="manualInactiveId" label="">
                    <>
                      <FormikControl type="checkbox" name="manualInactive" label="Urządzenie nieaktywne"/>
                      <small className="form-text text-muted">Nieaktywne urządzenie nie będzie widoczne w raportach.</small>
                    </>
                  </FormRowGroup>
                  <FormRowGroup controlId="nisId" label="NIS">
                    <FormikControl type="text" name="nis"/>
                  </FormRowGroup>
                  {/*
                  <FormRowGroup controlId="clientId" label="Id klienta">
                    <FormikControl type="text" name="clientId"/>
                  </FormRowGroup>
                  */}
                  <FormRowGroup controlId="locationId" label="Lokacja">
                    <FormikControl id="printerLocationId" type="oracle" onSearch={getLocations} name="lwi"/>
                  </FormRowGroup>
                  <FormRowGroup controlId="descId" label="Opis">
                    <FormikControl type="textarea" name="description"/>
                  </FormRowGroup>
                  <FormRowGroup label="Dodatkowe pola" controlId="additionalId">
                    <FormikControl type="map" name="additionalData" />
                  </FormRowGroup>
                  <FormButtonGroup>
                    <Button variant="primary" disabled={isSubmitting || !isValid} type="submit">Zapisz</Button>
                    <Button variant="outline-secondary" onClick={handleReset}>Anuluj</Button>
                    {ctx.user.admin ?
                        <ButtonLink variant="outline-danger" to={DeletePrinter.buildUrl(printer.id)} withBackground={true}>Usuń</ButtonLink>
                        : null
                    }
                  </FormButtonGroup>
                </Form>
            )}
          </Formik>
        </div>

        <div className="settings-section printer-info">
          <h2>Informacje</h2>

          {printer.manualCounter && (
            <p className="manual-counter-callout">
              Liczniki tego urządzenia są obecnie wprowadzane ręcznie.
            </p>
          )}

          <dl className="info-list">
            <dt>Model</dt>
            <dd>{printer.model || '—'}</dd>
            <dt>Numer IP</dt>
            <dd>{printer.ip || '—'}</dd>
            <dt>MAC</dt>
            <dd>{printer.macNumber || '—'}</dd>
            <dt>Numer seryjny</dt>
            <dd className="info-value-with-action">
              <span>{printer.serialNumber || '—'}</span>
              {printer.serialNumber ? (
                <button
                  type="button"
                  className="inline-copy-btn"
                  onClick={handleCopySerialNumber}
                  title="Kopiuj numer seryjny"
                  aria-label="Kopiuj numer seryjny">
                  <Copy />
                </button>
              ) : null}
            </dd>
          </dl>

          <h3 className="info-subhead">Liczniki</h3>
          <div className="info-kpi-grid">
            <div className="info-kpi">
              <span className="info-kpi-label">Główny</span>
              <span className="info-kpi-value">{printer.totalCounter ?? '—'}</span>
            </div>
            {printer.monoCounter > 0 && (
              <div className="info-kpi">
                <span className="info-kpi-label">Mono</span>
                <span className="info-kpi-value">{printer.monoCounter}</span>
              </div>
            )}
            {printer.colorCounter > 0 && (
              <div className="info-kpi">
                <span className="info-kpi-label">Kolor</span>
                <span className="info-kpi-value">{printer.colorCounter}</span>
              </div>
            )}
            {printer.scanBWCounter > 0 && (
              <div className="info-kpi">
                <span className="info-kpi-label">Skany mono</span>
                <span className="info-kpi-value">{printer.scanBWCounter}</span>
              </div>
            )}
            {printer.scanColorCounter > 0 && (
              <div className="info-kpi">
                <span className="info-kpi-label">Skany kolor</span>
                <span className="info-kpi-value">{printer.scanColorCounter}</span>
              </div>
            )}
          </div>

          {hasTonerData && (
            <>
              <h3 className="info-subhead">Poziom tonerów</h3>
              <TonersInfo printer={printer} variant="bars"/>
            </>
          )}

          {serviceCauses.length > 0 && (
            <>
              <h3 className="info-subhead">Przyczyna serwisowa</h3>
              <p className="info-alerts">{serviceCausesFormatted}</p>
            </>
          )}

          {jamLocations.length > 0 && (
            <>
              <h3 className="info-subhead">Miejsce zacięcia</h3>
              <p className="info-alerts">{jamLocations.join(', ')}</p>
            </>
          )}

          {offlineCauses.length > 0 && (
            <>
              <h3 className="info-subhead">Przyczyna niedostępności</h3>
              <p className="info-alerts">{offlineCauses.join(', ')}</p>
            </>
          )}

          {alertsList.length > 0 && (
            <>
              <h3 className="info-subhead">Alerty</h3>
              <p className="info-alerts">{alertsFormatted}</p>
            </>
          )}

          <dl className="info-list info-updates">
            <dt>Ostatni kontakt</dt>
            <dd>{printer.lastContactedRaw ? formatDate(printer.lastContactedRaw) : '—'}</dd>
            <dt>Ostatnia zmiana liczników</dt>
            <dd>{printer.lastCountersUpdateRaw ? formatDate(printer.lastCountersUpdateRaw) : '—'}</dd>
            <dt>Ostatnia zmiana stanu</dt>
            <dd>{printer.lastAlertsUpdateRaw ? formatDate(printer.lastAlertsUpdateRaw) : '—'}</dd>
          </dl>
        </div>
      </div>

      <PrinterNotifications printerId={printer.id} />
      <PrinterPanels printer={printer.id} />
    </>;
}

PrintersDetails.url = '/printers/:id';

export default withRouter(PrintersDetails);
